/** 问答记录：当前用户自己的历史会话列表 (卡片) + 点开查看完整对话。 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, ChevronDown, MessageSquareText, MessagesSquare } from "lucide-react";
import { Alert, EmptyState, PageHeader, Spinner, buttonVariants } from "@/components/ui";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { ChatBubble } from "../components/ChatBubble";
import { QuoteCard } from "../components/QuoteCard";
import { fmtMoney, quoteSnapshotFromStored } from "../quoteFormat";
import { listMySessions, fetchSession } from "../api";
import type { SessionSummary } from "../api";

/** "2026-10-04T10:00:03" → "2026-10-04 10:00" */
function fmt(ts: string): string {
  return ts.replace("T", " ").slice(0, 16);
}

export function HistoryPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["quote", "my-sessions"],
    queryFn: () => listMySessions(),
  });
  const [openId, setOpenId] = useState<string | null>(null);

  const sessions = data?.data ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6 md:py-8">
      <PageHeader
        title="问答记录"
        description="你在智能报价里的历史对话，按最近活跃排序。"
      />

      {error && <Alert>{error instanceof ApiError ? `加载失败 (${error.status})` : "加载失败"}</Alert>}

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Spinner /> 加载中…
        </div>
      )}

      {!isLoading && !error && sessions.length === 0 && (
        <div className="rounded-xl border bg-card shadow-sm">
          <EmptyState
            icon={MessagesSquare}
            title="还没有问答记录"
            hint="去智能报价里问一句试试，对话会自动保存在这里。"
            action={
              <Link to="/" className={buttonVariants({ size: "sm" })}>
                <MessageSquareText aria-hidden />
                去报价
              </Link>
            }
          />
        </div>
      )}

      <ul className="space-y-2.5">
        {sessions.map((s) => (
          <SessionCard
            key={s.session_id}
            session={s}
            open={openId === s.session_id}
            onToggle={() => setOpenId(openId === s.session_id ? null : s.session_id)}
          />
        ))}
      </ul>
    </div>
  );
}

function SessionCard({ session, open, onToggle }: {
  session: SessionSummary;
  open: boolean;
  onToggle: () => void;
}) {
  const detail = useQuery({
    queryKey: ["quote", "session", session.session_id],
    queryFn: () => fetchSession(session.session_id),
    enabled: open,
  });
  const panelId = `session-${session.session_id}`;

  return (
    <li className={cn("overflow-hidden rounded-xl border bg-card shadow-sm transition-shadow", open && "shadow-md")}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring md:px-5"
      >
        <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
          <MessageSquareText className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground">
            {session.preview || "（无内容）"}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5 text-caption text-muted-foreground">
            <CalendarClock aria-hidden className="h-3.5 w-3.5" />
            <span className="tabular-nums">{fmt(session.last_ts)}</span>
            <span aria-hidden>·</span>
            <span className="tabular-nums">{session.turn_count} 条消息</span>
            {session.quote_count > 1 && (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">{session.quote_count} 次报价</span>
              </>
            )}
          </span>
        </span>
        {session.quote_total != null && (
          <span className="shrink-0 text-right">
            <span className="block text-caption text-muted-foreground">
              {session.quote_count > 1 ? "最近报价" : "报价合计"}
            </span>
            <span className="block text-base font-semibold tabular-nums tracking-tight text-price">
              ¥{fmtMoney(session.quote_total)}
            </span>
          </span>
        )}
        <ChevronDown
          aria-hidden
          className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div id={panelId} className="space-y-3 border-t bg-background px-3 py-4 md:px-5">
          {detail.isLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Spinner /> 加载中…
            </div>
          )}
          {detail.error && <Alert>加载失败</Alert>}
          {detail.data?.turns.map((t) => {
            const quote = t.role === "assistant" ? quoteSnapshotFromStored(t.quote) : null;
            return (
              <ChatBubble key={t.seq} role={t.role} content={t.content}>
                {quote && (
                  <QuoteCard
                    details={quote.details}
                    totalPrice={quote.totalPrice}
                    extractedParams={quote.extractedParams}
                    time={t.ts}
                  />
                )}
              </ChatBubble>
            );
          })}
          {detail.data && detail.data.turns.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">该会话已被清空。</div>
          )}
        </div>
      )}
    </li>
  );
}
